import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { AnalyticsProductDto, AnalyticsProductsResponseDto } from "@normax-performance/shared-types";
import { SortableColumnHeader, useSortableRows } from "../../../shared-ui/primitives";
import { useLocale } from "../../../i18n/LocaleProvider";
import { formatAnalyticsMinutes, formatAnalyticsPercent } from "../../../lib/format";

export interface ProductsTableProps {
  response: AnalyticsProductsResponseDto;
}

/**
 * Product analytics (docs/.../FORENSIC_PRODUCT_BRIEF.md §8, this slice's
 * spec §15). Deliberately NO quantity column — `AnalyticsProductDto` itself
 * carries no quantity field at all (backend Slice A decision: mixed Units
 * per product make a single summed total misleading), so there is nothing
 * to accidentally render here.
 */
export function ProductsTable({ response }: ProductsTableProps) {
  const { t } = useTranslation("analytics");
  const { intlLocale } = useLocale();

  // "Standardni min" (earned standard minutes) removed — Method C intermediate,
  // not business-facing (task: time-distribution simplification §7).
  const columns = {
    code: (p: AnalyticsProductDto) => p.code,
    name: (p: AnalyticsProductDto) => p.name,
    normedMinutes: (p: AnalyticsProductDto) => Number(p.normedMinutes),
    performance: (p: AnalyticsProductDto) => (p.performancePercentLabel === null ? null : Number(p.performancePercentLabel)),
    workers: (p: AnalyticsProductDto) => p.workerCount,
    operations: (p: AnalyticsProductDto) => p.operationCount,
  };
  const { sorted, sortKey, direction, requestSort } = useSortableRows(response.products, columns, {
    key: "normedMinutes",
    direction: "desc",
  });

  return (
    <div className="nx-analytics-table-wrap nx-scroll-table">
      <table className="nx-analytics-table nx-table">
        <thead>
          <tr>
            <SortableColumnHeader label={t("products.code")} sortKey="code" activeSortKey={sortKey} direction={direction} onSort={requestSort} />
            <SortableColumnHeader label={t("products.product")} sortKey="name" activeSortKey={sortKey} direction={direction} onSort={requestSort} />
            <SortableColumnHeader label={t("products.normedMinutes")} sortKey="normedMinutes" activeSortKey={sortKey} direction={direction} onSort={requestSort} className="nx-analytics-table__num" />
            <SortableColumnHeader label={t("products.performance")} sortKey="performance" activeSortKey={sortKey} direction={direction} onSort={requestSort} className="nx-analytics-table__num" />
            <SortableColumnHeader label={t("products.workers")} sortKey="workers" activeSortKey={sortKey} direction={direction} onSort={requestSort} className="nx-analytics-table__num" />
            <SortableColumnHeader label={t("products.operations")} sortKey="operations" activeSortKey={sortKey} direction={direction} onSort={requestSort} className="nx-analytics-table__num" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => (
            <tr key={p.productId}>
              <td>{p.code ?? "—"}</td>
              <td>
                {p.productId ? (
                  <Link to={`/izvestaji?type=PRODUCT&productId=${p.productId}&from=${response.period.from}&to=${response.period.to}`}>{p.name}</Link>
                ) : (
                  p.name
                )}
              </td>
              <td className="nx-analytics-table__num">{formatAnalyticsMinutes(p.normedMinutes, intlLocale)}</td>
              <td className="nx-analytics-table__num">
                <span className={`nx-analytics-table__cell-readout${p.zone ? ` nx-analytics-table__cell-readout--${p.zone.zone}` : ""}`}>
                  {formatAnalyticsPercent(p.performancePercentLabel, intlLocale)}
                </span>
              </td>
              <td className="nx-analytics-table__num">{p.workerCount}</td>
              <td className="nx-analytics-table__num">{p.operationCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
